import { useMemo, useRef, useState } from "react";
import { api, apiError, formatApiError, uploadFile } from "@/lib/api";
import { SignedImg } from "@/components/shared/SignedImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Pencil, Plus, ExternalLink } from "lucide-react";
import { domain } from "./helpers";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || apiError(e));
const EMPTY = { name: "", url: "", supplier: "", category: "", image_url: "" };

// Podzáložka „Seznam dekorů" — firemní vzorník (§1). Přeneseno z původního modulu.
export function DecorList({ decors, cats, usage, canVzornik, onChanged, onManageCats }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prevBusy, setPrevBusy] = useState(false);
  const [filter, setFilter] = useState("Vše");
  const fileRef = useRef(null);

  const decorCats = useMemo(() => {
    const m = {};
    decors.forEach((d) => { const k = d.category || "Nezařazeno"; m[k] = (m[k] || 0) + 1; });
    const arr = Object.entries(m).filter(([k]) => k !== "Nezařazeno").sort((a, b) => a[0].localeCompare(b[0], "cs"));
    if (m["Nezařazeno"]) arr.push(["Nezařazeno", m["Nezařazeno"]]);
    return arr;
  }, [decors]);
  const visible = filter === "Vše" ? decors : decors.filter((d) => (d.category || "Nezařazeno") === filter);

  const pickFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const u = await uploadFile(f); setForm((p) => ({ ...p, image_url: u })); }
    catch (e2) { err(e2); } finally { setBusy(false); }
  };
  const save = async () => {
    if (!form.name.trim() || !form.url.trim()) return;
    setBusy(true);
    try {
      if (form.id) {
        const body = { name: form.name, url: form.url, supplier: form.supplier, category: form.category };
        if (form.image_url !== form._origImage) body.image_url = form.image_url || null;
        await api.patch(`/decors/${form.id}`, body);
      } else {
        await api.post("/decors", { name: form.name, url: form.url, supplier: form.supplier, category: form.category, image_url: form.image_url || null });
      }
      setForm(null); onChanged();
    } catch (e2) { err(e2); } finally { setBusy(false); }
  };
  const fetchPreview = async () => {
    if (!form || !form.url.trim() || form.image_url) return;
    setPrevBusy(true);
    try {
      const { data: p } = await api.post("/decors/preview", { url: form.url.trim() });
      setForm((cur) => cur ? { ...cur, image_url: (p.image_url && !cur.image_url) ? p.image_url : cur.image_url, name: (p.title && !cur.name.trim()) ? p.title : cur.name } : cur);
    } catch { /* ticho */ } finally { setPrevBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap mb-3.5">
        <p className="text-sm text-muted-foreground flex-1">
          Firemní vzorník — {decors.length} dekorů. Co je tady, to jde vybrat u položky pasportu; odkaz a barva se doplní samy.
        </p>
        {canVzornik && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onManageCats} className="h-[42px] font-bold" data-testid="cats-manage-btn">Spravovat kategorie</Button>
            {!form && <Button onClick={() => setForm({ ...EMPTY })} className="h-[42px] font-bold" data-testid="decor-add-btn"><Plus size={16} /> Přidat dekor</Button>}
          </div>
        )}
      </div>

      {decorCats.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar flex-nowrap mb-3.5 -mx-1 px-1">
          {[["Vše", decors.length], ...decorCats].map(([c, n]) => (
            <button key={c} type="button" onClick={() => setFilter(c)} data-testid={`decor-filter-${c}`}
              className={`h-9 px-3.5 rounded-full text-[13px] font-bold whitespace-nowrap border transition-colors shrink-0 ${
                filter === c ? "bg-brand text-white border-brand" : "bg-card text-muted-foreground border-border hover:text-foreground"}`}>
              {c} ({n})
            </button>
          ))}
        </div>
      )}

      {form && (
        <div className="border border-border rounded-[14px] p-4 mb-3.5" data-testid="decor-form">
          <h3 className="font-extrabold mb-3">{form.id ? "Upravit dekor" : "Nový dekor do vzorníku"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_1.6fr_1fr_1fr] gap-3 items-end">
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Název <span className="text-brand">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="např. W980 Platinová bílá" className="h-11 mt-1" data-testid="decor-name" /></div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Odkaz na produkt <span className="text-brand">*</span></Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} onBlur={fetchPreview} placeholder="https://…" className="h-11 mt-1" data-testid="decor-url" />
              {prevBusy && <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground mt-1" data-testid="decor-preview-spinner">
                <span className="w-3.5 h-3.5 rounded-full border-2 border-secondary border-t-brand animate-spin motion-reduce:animate-none inline-block" /> Načítám náhled…</span>}</div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Dodavatel</Label>
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="Kronospan" className="h-11 mt-1" data-testid="decor-supplier" /></div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Kategorie</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} list="pasport-cats-list" placeholder="Dekor DTD" className="h-11 mt-1" data-testid="decor-category" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_1.6fr_1fr_1fr] gap-3 items-end mt-3">
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Fotka vzorku (doplní se sama z odkazu)</Label>
              <Input value={form.image_url?.startsWith("/api/files/") ? "" : form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…/vzorek.jpg" className="h-11 mt-1" data-testid="decor-image-url" /></div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="decor-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="h-11 w-full border-dashed font-semibold text-muted-foreground" data-testid="decor-file-btn">{busy ? "Nahrávám…" : "Vybrat soubor"}</Button>
            </div>
            <div className="sm:col-span-2 lg:col-span-2">
              {form.image_url ? (
                <button type="button" onClick={() => setForm({ ...form, image_url: "" })} title="Klik smaže obrázek" className="h-11 w-full rounded-[11px] border border-border overflow-hidden" data-testid="decor-preview-thumb">
                  {form.image_url.startsWith("/api/files/") ? <SignedImg url={form.image_url} alt="" className="w-full h-full object-cover" /> : <img src={form.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                </button>
              ) : <div className="h-11 rounded-[11px] border border-border grid place-items-center text-xs font-bold text-muted-foreground">bez fotky</div>}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-3.5">
            {(!form.name.trim() || !form.url.trim()) && <span className="text-[13px] font-bold text-destructive">Název i odkaz na produkt jsou povinné.</span>}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setForm(null)} className="h-11 font-bold">Zrušit</Button>
            <Button onClick={save} disabled={busy || !form.name.trim() || !form.url.trim()} className="h-11 font-bold" data-testid="decor-save-btn">{busy ? "Ukládám…" : "Uložit dekor"}</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
        {visible.map((d) => (
          <div key={d.id} className="border border-border rounded-[14px] overflow-hidden bg-card flex flex-col" data-testid={`decor-card-${d.id}`}>
            <div className="h-[120px] border-b border-border bg-secondary relative">
              {(d.thumb_url || d.image_url) ? <SignedImg url={d.thumb_url || d.image_url} alt="" className="w-full h-full object-cover" />
                : <span className="absolute bottom-2 right-2 text-[11px] font-bold text-muted-foreground bg-card rounded-full px-2 py-0.5">bez fotky vzorku</span>}
            </div>
            <div className="p-3 flex-1 flex flex-col gap-1">
              <div className="font-bold text-[15px] leading-snug">{d.name}</div>
              <div className="text-[13px] text-muted-foreground">{d.supplier} · {d.category}</div>
              <a href={d.url} target="_blank" rel="noreferrer" className="text-[13px] font-bold text-brand inline-flex items-center gap-1">{domain(d.url)} <ExternalLink size={12} /></a>
              <div className="flex items-center justify-between mt-auto pt-2">
                <span className="text-xs font-bold text-muted-foreground bg-secondary rounded-full px-2.5 py-1">v pasportu {usage[d.id] || 0}×</span>
                {canVzornik && <Button variant="outline" onClick={() => setForm({ id: d.id, name: d.name, url: d.url, supplier: d.supplier === "Bez dodavatele" ? "" : d.supplier, category: d.category === "Nezařazeno" ? "" : d.category, image_url: d.image_url || "", _origImage: d.image_url || "" })} className="h-[34px] px-3 text-[13px] font-semibold" data-testid={`decor-edit-${d.id}`}><Pencil size={13} /> Upravit</Button>}
              </div>
            </div>
          </div>
        ))}
        {canVzornik && (
          <button type="button" onClick={() => setForm({ ...EMPTY })} className="min-h-[200px] rounded-[14px] border border-dashed border-border text-muted-foreground hover:bg-secondary/50 flex flex-col items-center justify-center gap-1" data-testid="decor-add-tile">
            <span className="text-[15px] font-semibold">+ Přidat dekor</span><span className="text-[13px]">název, odkaz, fotka</span>
          </button>
        )}
        {decors.length === 0 && !canVzornik && <p className="text-sm text-muted-foreground">Vzorník je zatím prázdný.</p>}
      </div>
    </div>
  );
}
